/**
 * Single source of truth for the canonical payment-status partition.
 *
 * Both ``SalePage``'s auto-refresh poll and ``PaymentLinkPanel``'s
 * "show only while async flow is in flight" gate need to know whether
 * a payment row is still in flight. Hardcoding the set in each call
 * site invited drift - a new in-flight status (e.g. CHARGEBACK, HOLD)
 * could make polling stop while the link panel kept rendering, or
 * vice versa.
 *
 * The strings mirror tilt-runtime's ``PaymentStatus`` enum
 * (results.py) - they're the values the API serialises to
 * ``OrderPayment.status``.
 *
 *   non-terminal  = PENDING | ACTION_REQUIRED
 *                   the customer-facing flow may still complete;
 *                   keep polling, keep showing the payment-link panel
 *
 *   terminal      = everything else
 *                   the row's status will not change without an
 *                   operator action (refund, void, manual update)
 */

import type { OrderPayment } from "@tilt/pos-sdk";

export const NON_TERMINAL_PAYMENT_STATUSES: ReadonlySet<string> = new Set([
  "PENDING",
  "ACTION_REQUIRED",
]);

export function isNonTerminalPaymentStatus(status: string): boolean {
  return NON_TERMINAL_PAYMENT_STATUSES.has(status);
}

export function isTerminalPaymentStatus(status: string): boolean {
  return !isNonTerminalPaymentStatus(status);
}

/** Sum of in-flight charge amounts on an order. ``order.remaining_cents``
 *  does NOT subtract these (the backend only counts settled /
 *  partially-settled rows), so any caller that wants a true
 *  "what's left to collect" figure must subtract this client-side.
 *  Used by OrderSummary's Remaining stat AND by the SalePage /
 *  OrderDetailPage MethodPicker cap so a second payment cannot
 *  overcharge once the in-flight one settles.
 *
 *  Refunds never enter the set - only ``role === "charge"`` rows.
 *  ``requested_amount_cents`` is preferred (the figure the cashier
 *  committed to on Confirm); falls back to ``applied_amount_cents``
 *  for older rows that don't carry it. */
export function sumNonTerminalChargeCents(payments: readonly OrderPayment[]): number {
  let total = 0;
  for (const p of payments) {
    if (p.role !== "charge") continue;
    if (!isNonTerminalPaymentStatus(p.status)) continue;
    total += p.requested_amount_cents ?? p.applied_amount_cents;
  }
  return total;
}

/** Count of distinct in-flight charge rows on an order. Used to pluralise
 *  the "payments in flight" banner when more than one payment is pending. */
export function countNonTerminalCharges(payments: readonly OrderPayment[]): number {
  return payments.filter((p) => p.role === "charge" && isNonTerminalPaymentStatus(p.status)).length;
}

/** Cashier-side "this is taking too long" threshold. Drops a stuck
 *  Valor card-present row out of "in flight" and into a visible
 *  warning so the cashier knows to void + retry instead of waiting
 *  on a hung terminal.
 *
 *  Currently 90 seconds (2026-05-03 user direction) - short enough
 *  that a stuck terminal pops up in roughly the same window a
 *  customer would give up at the device, long enough that a healthy
 *  card-present transaction still completes inside the cap on the
 *  happy path. Aeropay ACH typically lands in 30-60s on dev; even
 *  the slow tail rarely exceeds 90s, so the false-positive rate is
 *  acceptable until we land the per-payment ``valor_deadline_at``
 *  backend guard.
 *
 *  Backend cancellation / TTL / late-completion reversal is a
 *  separate guard (#192/#193); this constant is purely the UI's
 *  "show a stronger banner now" threshold. */
export const PENDING_PAYMENT_TIMEOUT_MS = 90 * 1000;

/** Age (ms) of the OLDEST non-terminal charge on the order, or null
 *  if there isn't one. Caller compares against ``PENDING_PAYMENT_TIMEOUT_MS``
 *  to decide whether to surface the timed-out warning. ``created_at``
 *  is the canonical "when did the cashier hit Confirm" timestamp; we
 *  pick the oldest pending row so a series of partial pays escalates
 *  on the FIRST stuck slice, not the most recent. */
export function oldestNonTerminalChargeAgeMs(
  payments: readonly OrderPayment[],
  now: number = Date.now(),
): number | null {
  let oldest: number | null = null;
  for (const p of payments) {
    if (p.role !== "charge") continue;
    if (!isNonTerminalPaymentStatus(p.status)) continue;
    const created = Date.parse(p.created_at);
    if (Number.isNaN(created)) continue;
    if (oldest === null || created < oldest) oldest = created;
  }
  return oldest === null ? null : Math.max(0, now - oldest);
}
